// Client access, shared by the Clients list and the client page: who can log in for a company, their
// invitations, the primary (finance) contact, and assigned reps. `company` may come from either API —
// the list gives store as { code }, the client page as a plain code.
import { useEffect, useState } from 'react';
import {
  fetchCompanyDetail,
  provisionClient,
  provisionRep,
  assignRep,
  setContactPrimary,
  bulkInvite,
} from '../api/companies.js';

const errMsg = (e) => e.response?.data?.error?.message || 'Something went wrong.';
const storeCodeOf = (company) => company.store?.code ?? company.store;

export function Chip({ children, tone = 'default' }) {
  const tones = {
    default: 'bg-surface-raised text-text-secondary',
    accent: 'bg-accent-light text-text-primary',
    muted: 'bg-surface-raised text-text-muted',
    danger: 'bg-error-light text-error',
  };
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${tones[tone]}`}>{children}</span>;
}

export function Btn({ children, onClick, busy, variant = 'secondary', disabled, type = 'button' }) {
  const styles =
    variant === 'primary'
      ? 'bg-accent text-white hover:bg-accent-hover'
      : 'border border-line text-text-secondary hover:bg-surface-raised hover:text-text-primary';
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={busy || disabled}
      className={`rounded-md px-3 py-1.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${styles}`}
    >
      {busy ? '…' : children}
    </button>
  );
}

// --- Add a platform-only "buyer": a login that can order for the company but has NO Shopify contact.
// (Contrast with LinkClientPanel, which ties a login to an existing Shopify contact.) ---
export function AddBuyerPanel({ company, onDone, onCancel }) {
  const locations = company.locations || [];
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [locId, setLocId] = useState(locations[0]?.id || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [invite, setInvite] = useState(null);

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      // No shopifyCompanyContactId → provisioned as a platform-only buyer.
      const res = await provisionClient({ email: email.trim(), name: name.trim() || undefined, companyLocationId: locId });
      setInvite({ email: email.trim(), url: res.inviteUrl, emailSent: res.emailSent });
      onDone?.();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = /.+@.+\..+/.test(email) && locId;
  const input = 'w-full rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30';

  if (invite) {
    return (
      <div className="mt-2 rounded-md border border-accent/40 bg-accent-light/50 p-3 text-xs">
        <div className="mb-1 font-medium text-text-primary">
          {invite.emailSent ? `Buyer added — login instructions emailed to ${invite.email}` : `Buyer added — couldn't email ${invite.email}, share this link:`}
        </div>
        <div className="flex items-center gap-2">
          <input readOnly value={invite.url} onFocus={(e) => e.target.select()} className="min-w-0 flex-1 rounded border border-line bg-surface px-2 py-1 font-mono text-[11px]" />
          <button type="button" onClick={() => navigator.clipboard?.writeText(invite.url)} className="shrink-0 rounded border border-line px-2 py-1 font-medium text-text-secondary hover:bg-surface">Copy</button>
        </div>
        <button type="button" onClick={onCancel} className="mt-2 rounded-md border border-line px-2.5 py-1 font-medium text-text-secondary hover:bg-surface">Done</button>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-md border border-line bg-surface-raised/50 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium text-text-secondary">Add a buyer (platform login — no Shopify account needed)</span>
        <button type="button" onClick={onCancel} className="text-xs text-text-muted hover:text-text-primary">Cancel</button>
      </div>
      {error && <p className="mb-2 text-xs text-error">{error}</p>}
      <div className="grid gap-2 sm:grid-cols-2">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="buyer@company.com" className={input} />
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" className={input} />
      </div>
      {locations.length > 1 && (
        <select value={locId} onChange={(e) => setLocId(e.target.value)} className={`mt-2 ${input}`}>
          {locations.map((l) => (
            <option key={l.id} value={l.id}>{l.name}{l.currencyCode ? ` · ${l.currencyCode}` : ''}</option>
          ))}
        </select>
      )}
      <div className="mt-2">
        <Btn variant="primary" busy={busy} disabled={!canSubmit} onClick={submit}>Add buyer</Btn>
      </div>
    </div>
  );
}

// --- Link a client login from an existing Shopify contact (company-wide, not per location) ---
export function LinkClientPanel({ company, onDone, onCancel }) {
  const locations = company.locations || [];
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [invite, setInvite] = useState(null); // { email, url } after provisioning
  // A login still resolves a catalogue via a location; default to the first (only shown when >1).
  const [locId, setLocId] = useState(locations[0]?.id || '');

  useEffect(() => {
    fetchCompanyDetail(storeCodeOf(company), company.shopifyCompanyId)
      .then(setDetail)
      .catch((e) => setError(errMsg(e)));
  }, [company]);

  const link = async (contact) => {
    setBusyId(contact.shopifyCompanyContactId);
    setError('');
    try {
      const res = await provisionClient({
        email: contact.email,
        name: contact.name || undefined,
        companyLocationId: locId,
        shopifyCompanyContactId: contact.shopifyCompanyContactId,
      });
      setInvite({ email: contact.email, url: res.inviteUrl, emailSent: res.emailSent });
    } catch (e) {
      setError(errMsg(e));
      setBusyId(null);
    }
  };

  if (invite) {
    return (
      <div className="mt-2 rounded-md border border-accent/40 bg-accent-light/50 p-3 text-xs">
        <div className="mb-1 font-medium text-text-primary">
          {invite.emailSent
            ? `Client login created — login instructions emailed to ${invite.email}`
            : `Client login created — couldn't email ${invite.email}, share this link:`}
        </div>
        <div className="flex items-center gap-2">
          <input readOnly value={invite.url} onFocus={(e) => e.target.select()} className="min-w-0 flex-1 rounded border border-line bg-surface px-2 py-1 font-mono text-[11px]" />
          <button type="button" onClick={() => navigator.clipboard?.writeText(invite.url)} className="shrink-0 rounded border border-line px-2 py-1 font-medium text-text-secondary hover:bg-surface">
            Copy
          </button>
        </div>
        <p className="mt-1 text-text-muted">They set a password, then sign in with email + password + a one-time code.</p>
        <button type="button" onClick={onDone} className="mt-2 rounded-md border border-line px-2.5 py-1 font-medium text-text-secondary hover:bg-surface">
          Done
        </button>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-md border border-line bg-surface-raised/50 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium text-text-secondary">
          Link a Shopify contact as a client login
        </span>
        <button type="button" onClick={onCancel} className="text-xs text-text-muted hover:text-text-primary">
          Cancel
        </button>
      </div>
      {error && <p className="mb-2 text-xs text-error">{error}</p>}
      {locations.length > 1 && (
        <div className="mb-2">
          <label className="mb-1 block text-[11px] font-medium text-text-muted">Location this login orders for</label>
          <select
            value={locId}
            onChange={(e) => setLocId(e.target.value)}
            className="w-full rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
          >
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.name}{l.currencyCode ? ` · ${l.currencyCode}` : ''}</option>
            ))}
          </select>
        </div>
      )}
      {!detail ? (
        <p className="text-xs text-text-muted">Loading contacts…</p>
      ) : detail.contacts.length === 0 ? (
        <p className="text-xs text-text-muted">No contacts on this company in Shopify.</p>
      ) : (
        <ul className="space-y-1.5">
          {detail.contacts.map((c) => (
            <li key={c.shopifyCompanyContactId} className="flex items-center justify-between gap-3">
              <span className="truncate text-sm text-text-primary">
                {c.email || '(no email)'} {c.name && <span className="text-text-muted">· {c.name}</span>}
              </span>
              <Btn variant="primary" busy={busyId === c.shopifyCompanyContactId} disabled={!c.email || !locId} onClick={() => link(c)}>
                Provision login
              </Btn>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// --- Assign a rep to a company (provision-if-new by email) ---
export function AssignRepForm({ company, onDone }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const { user } = await provisionRep({ email: email.trim() });
      await assignRep({ repUserId: user.id, companyId: company.id });
      setEmail('');
      onDone();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-2 flex items-center gap-2">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="rep@email.com"
        className="w-56 rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
      />
      <Btn type="submit" busy={busy} disabled={!email.trim()}>
        Assign rep
      </Btn>
      {error && <span className="text-xs text-error">{error}</span>}
    </form>
  );
}

// Every login on the company, per location, with one "Send invitations" button for all of them, the
// primary-contact star (who gets statements & finance mail), and the two ways to add a login.
export function ClientLogins({ company, onChange, readOnly = false, showLocations = true }) {
  const [linking, setLinking] = useState(false);
  const [addBuyer, setAddBuyer] = useState(false);
  const [inviteState, setInviteState] = useState(null); // null | 'sending' | { emailed, failed } | { error }
  const locations = company.locations || [];
  const loginCount = locations.reduce((n, l) => n + (l.contacts || []).filter((ct) => ct.user).length, 0);
  const pending = locations.reduce((n, l) => n + (l.contacts || []).filter((ct) => ct.user?.isActive && !ct.user.registeredAt).length, 0);

  const sendAllInvites = async () => {
    setInviteState('sending');
    try {
      setInviteState(await bulkInvite([company.id]));
    } catch {
      setInviteState({ error: true });
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-medium text-text-muted">
          Client logins · {loginCount}
          {pending > 0 && <span className="text-warning"> · {pending} haven’t set up their account</span>}
        </span>
        {!readOnly && (
          <div className="flex flex-wrap items-center gap-3">
            {inviteState === 'sending' ? (
              <span className="text-[11px] text-text-muted">Sending…</span>
            ) : inviteState?.error ? (
              <span className="text-[11px] text-error">Couldn’t send — try again</span>
            ) : inviteState ? (
              <span className="text-[11px] text-success">✓ {inviteState.emailed ?? 0} sent{inviteState.failed ? ` · ${inviteState.failed} failed` : ''}</span>
            ) : null}
            {loginCount > 0 && (
              <button
                type="button"
                onClick={sendAllInvites}
                className="text-xs font-medium text-accent hover:underline"
                title="Email account-setup invitations to everyone who hasn't set up their login yet (all locations)"
              >
                Send invitations
              </button>
            )}
            <button type="button" onClick={() => setLinking((v) => !v)} className="text-xs font-medium text-accent hover:underline">
              {linking ? 'Close' : '+ Link Shopify contact'}
            </button>
            <button type="button" onClick={() => setAddBuyer((v) => !v)} className="text-xs font-medium text-accent hover:underline">
              {addBuyer ? 'Close' : '+ Add buyer'}
            </button>
          </div>
        )}
      </div>
      {linking && (
        <LinkClientPanel
          company={company}
          onCancel={() => setLinking(false)}
          onDone={() => {
            setLinking(false);
            onChange();
          }}
        />
      )}
      {addBuyer && <AddBuyerPanel company={company} onDone={onChange} onCancel={() => setAddBuyer(false)} />}

      {showLocations && (
        <div className="mt-3 space-y-2">
          {locations.map((loc) => {
            const contacts = (loc.contacts || []).filter((ct) => ct.user);
            return (
              <div key={loc.id} className="rounded-md border border-line p-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-text-primary">{loc.name}</span>
                  {loc.currencyCode && <Chip tone="muted">{loc.currencyCode}</Chip>}
                </div>
                {contacts.length > 0 ? (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {contacts.map((ct) => (
                      <span key={ct.id} className="inline-flex items-center gap-1 rounded bg-accent-light px-1.5 py-0.5 text-[11px] text-text-secondary">
                        {ct.isPrimary && (
                          <span className="font-semibold text-accent" title="Primary — receives statements & finance mail">
                            ★
                          </span>
                        )}
                        {ct.user.email}
                        {!ct.user.isActive && ' (inactive)'}
                        {ct.user.isActive && !ct.user.registeredAt && (
                          <span className="text-[10px] text-warning" title="Hasn't set up their account yet">
                            · invite pending
                          </span>
                        )}
                        {!ct.isPrimary && !readOnly && (
                          <button
                            type="button"
                            onClick={async () => {
                              await setContactPrimary(company.id, ct.id).catch(() => {});
                              onChange();
                            }}
                            className="ml-0.5 text-[10px] text-accent hover:underline"
                            title="Make this the primary contact — they get statements & finance mail"
                          >
                            make primary
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="mt-1 text-[11px] text-text-muted">No logins for this location yet.</p>
                )}
              </div>
            );
          })}
        </div>
      )}
      {loginCount > 0 && <p className="mt-2 text-[11px] text-text-muted">★ = primary contact: receives statements and finance emails.</p>}
    </div>
  );
}
